import { useTranslation } from 'react-i18next'
import { useMarket } from '@/contexts/MarketContext'
import { formatNumber, formatPercentChange } from '@/lib/format'
import { cn } from '@/lib/cn'

function priceDigits(n: number): number {
  if (n >= 1000) return 2
  if (n >= 1) return 4
  if (n >= 0.01) return 4
  return 8
}

function formatPx(n: number): string {
  return formatNumber(n, {
    minimumFractionDigits: Math.min(2, priceDigits(n)),
    maximumFractionDigits: priceDigits(n),
  })
}

/**
 * Symbol details under the buy/sell ticket — bid/ask, session stats, meta.
 */
export function SymbolInfoPanel({ className }: { className?: string }) {
  const { t } = useTranslation('trading')
  const { active } = useMarket()
  const up = active.changePct >= 0
  const spread = active.ask - active.bid
  const low = Math.min(active.open, active.bid, active.ask)

  const rows: { label: string; value: string; tone?: 'up' | 'down' }[] = [
    { label: t('symbolInfo.bid'), value: formatPx(active.bid) },
    { label: t('symbolInfo.ask'), value: formatPx(active.ask) },
    { label: t('symbolInfo.spread'), value: formatPx(spread) },
    { label: t('symbolInfo.last'), value: formatPx(active.last) },
    { label: t('symbolInfo.open'), value: formatPx(active.open) },
    { label: t('symbolInfo.high'), value: formatPx(active.high) },
    { label: t('symbolInfo.low'), value: formatPx(low) },
    {
      label: t('symbolInfo.change'),
      value: formatPercentChange(active.changePct),
      tone: up ? 'up' : 'down',
    },
    {
      label: t('symbolInfo.volume'),
      value: formatNumber(active.volume),
    },
    {
      label: t('symbolInfo.category'),
      value: t(`watchlist.filter${capitalize(active.category)}`),
    },
    { label: t('symbolInfo.currency'), value: active.currency },
    { label: t('symbolInfo.tvSymbol'), value: active.tvSymbol },
  ]

  return (
    <section
      className={cn(
        'flex flex-col border-t border-terminal-border/40',
        className,
      )}
    >
      <header className="flex items-center justify-between gap-2 px-3 py-2">
        <h2 className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
          {t('symbolInfo.title')}
        </h2>
        <span
          className={cn(
            'rounded-sm px-1.5 py-0.5 font-mono text-[10px] tabular-nums',
            up ? 'bg-bid-badge text-bid' : 'bg-danger/15 text-danger',
          )}
        >
          {up ? '▲' : '▼'} {formatPercentChange(Math.abs(active.changePct))}
        </span>
      </header>

      <div className="px-3 pb-2">
        <p className="font-mono text-sm font-semibold tracking-tight text-terminal-fg">
          {active.label}
        </p>
        <p className="mt-0.5 truncate text-[11px] text-terminal-muted">
          {active.name}
        </p>
      </div>

      <dl className="divide-y divide-terminal-border/30 border-t border-terminal-border/30">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-3 px-3 py-1.5"
          >
            <dt className="text-[10px] font-medium uppercase tracking-wider text-terminal-muted">
              {row.label}
            </dt>
            <dd
              className={cn(
                'truncate font-mono text-[11px] tabular-nums text-terminal-fg',
                row.tone === 'up' && 'text-bid',
                row.tone === 'down' && 'text-danger',
              )}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
